import * as React from 'react'
import PropTypes from 'prop-types'
import { MotionVariant } from '@ds/motion'
import { dataProps } from '@ds/react-utils'
import { CustomPropTypes } from '../../../support'
import { useIsInk, useThemeStyles, useThemeTokens } from '../../../theming'
import type { DivForwardRef } from '../../../types'
import { PopoverAlignment, PopoverLocation } from '../../Popover/index'
import styles from './styles'
import { PopoverArrow } from '../../../internal/components/PopoverArrow/PopoverArrow'

export type TooltipAlignment = PopoverAlignment
export type TooltipLocation = PopoverLocation

export interface TooltipUIProps {
  /**
   * The alignment of the Tooltip along the edge of its anchor element.
   *
   * For locations 'above' and 'below':
   * - 'start': start-aligns the Tooltip and the anchor element
   * - 'center': centers the Tooltip along the width of the anchor element
   * - 'end': end-aligns the Tooltip and the anchor element
   *
   * For locations 'before' and 'after':
   * - 'start': top-aligns the Tooltip and the anchor element
   * - 'center': centers the Tooltip along the height of the anchor element
   * - 'end': bottom-aligns the Tooltip and the anchor element
   */
  alignment?: TooltipAlignment
  /**
   * The length (in pixels) of the element the tooltip is positioned against.
   * Used to better position the pointer in scenarios where the default
   * positioning point to the anchor correctly - typically very small anchors.
   */
  anchorLength?: number
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the Tooltip component.
   */
  forwardedRef?: DivForwardRef
  /**
   * A unique ID.
   */
  id?: string
  /**
   * The preferred location of the Tooltip relative to its anchor element.
   */
  location?: TooltipLocation
  /**
   * The text to display inside the Tooltip.
   */
  text: string
}

export function TooltipUI({
  alignment = 'center',
  anchorLength,
  forwardedRef,
  id,
  location = 'above',
  text,
  ...restProps
}: TooltipUIProps) {
  const sx = useThemeStyles(styles, { alignment, location })
  const tokens = useThemeTokens()
  const isInk = useIsInk()

  /*
   * alignmentCenterArrow calculate the pointer positioning based on
   * the length of the side of the anchor the Tooltip is positioned against.
   *
   * anchorLength: Length of the side we want to point to in pixels
   * popoverArrowWidth: desired width of the PopoverArrow / 2
   *
   * 1. anchorLength / 2 is the center (c) of the anchor's side
   *
   *           c    V
   * |-------------------|
   *
   * 2. Minus the width of the arrow (to center the tip)
   *
   *           V
   * |-------------------|
   *
   * 3. Math.max to ensure the pointer's edge stays within the Tooltip.
   *
   */

  const popoverArrowWidth = 15 / 2

  const alignmentCenterArrow = anchorLength
    ? Math.max(anchorLength / 2 - Math.ceil(popoverArrowWidth), 1)
    : popoverArrowWidth

  return (
    <MotionVariant
      {...dataProps(restProps)}
      as="span"
      css={sx.default.tooltip}
      id={id}
      ref={forwardedRef}
      initial="initial"
      animate="enter"
      exit={['exitScale', 'exitOpacity']}
      variants={sx.motionVariants.tooltip}
      role="tooltip"
    >
      {text}
      <PopoverArrow
        alignment={alignment}
        backgroundColor={
          isInk ? tokens.messageBgColorDefault : tokens.bgColorInverse
        }
        borderColor={isInk ? tokens.borderColorEmphasisInverse : '#1e1e1e'}
        borderWidth={1}
        height={isInk ? 8 : 7}
        location={location}
        pointerPositionStyles
        offset={alignmentCenterArrow}
        width={isInk ? 15 : 12}
      />
    </MotionVariant>
  )
}

TooltipUI.propTypes = {
  alignment: PropTypes.string,
  anchorLength: PropTypes.number,
  'data-.*': PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  id: PropTypes.string,
  location: PropTypes.string,
  text: PropTypes.string.isRequired,
}

TooltipUI.displayName = 'TooltipUI'
